import React, { useState } from "react";
import { Play, X, GraduationCap, Building2 } from "lucide-react";
import { ASSETS } from "../mock";

const ABOUT_IMAGE = "/assets/images/2.png";

const VIDEO_THUMB =
  "https://images.unsplash.com/photo-1573165662973-4ab3cf3d3508?crop=entropy&cs=srgb&fm=jpg&q=85&w=600";

const VIDEO_URL =
  "https://www.youtube.com/embed/JqEjEh8X4Pg?autoplay=1";

const AboutSnippet = () => {
  const [videoOpen, setVideoOpen] = useState(false);

  return (
    <section className="relative py-24 bg-white overflow-hidden">
      <div className="max-w-[1280px] mx-auto px-6 grid lg:grid-cols-2 gap-14 items-center">
        {/* LEFT — IMAGE AREA */}
        <div className="relative flex items-center justify-center min-h-[560px]">
          {/* GREEN ORGANIC BLOB */}
          <svg
            className="absolute inset-0 w-full h-full animate-floatSlow"
            viewBox="0 0 600 600"
            preserveAspectRatio="xMidYMid meet"
            aria-hidden="true"
          >
            <defs>
              <linearGradient
                id="blobGrad"
                x1="0%"
                y1="0%"
                x2="100%"
                y2="100%"
              >
                <stop offset="0%" stopColor="#35b56a" />
                <stop offset="100%" stopColor="#1f9d4c" />
              </linearGradient>
            </defs>

            <path
              fill="url(#blobGrad)"
              d="M431,89.5c41,42,67,108,55,167s-62,111-115,138s-110,28-160,7s-95-65-117-118s-22-118,7-167s95-91,158-99S390,47.5,431,89.5z"
              transform="translate(20, 30)"
            />
          </svg>

          {/* DECORATIVE LINE */}
          <svg
            className="absolute inset-0 w-full h-full"
            viewBox="0 0 600 600"
            preserveAspectRatio="xMidYMid meet"
            aria-hidden="true"
          >
            <path
              d="M120 280 C 120 150, 240 80, 340 90 C 460 100, 530 200, 510 320 C 490 440, 380 510, 270 490 C 150 470, 90 380, 110 290"
              fill="none"
              stroke="#0a2463"
              strokeWidth="1.5"
              strokeLinecap="round"
              opacity="0.55"
            />
          </svg>

          {/* ACCENT DOTS */}
          <span className="absolute top-10 left-16 w-3 h-3 rounded-full bg-[#ff6b00] animate-pulse" />

          <span
            className="absolute bottom-20 right-12 w-4 h-4 rounded-full bg-[#ff6b00] animate-pulse"
            style={{ animationDelay: "0.6s" }}
          />

          <span className="absolute bottom-10 left-20 w-2.5 h-2.5 rounded-full bg-[#ff6b00]" />

          {/* IMAGE */}
          <div className="relative w-[78%] aspect-[3/4] z-10 flex items-end justify-center">
            <img
              src={ABOUT_IMAGE}
              alt="AKSUBEB leadership"
              className="w-full h-full object-cover object-top grayscale hover:grayscale-0 transition-all duration-700"
            />
          </div>

          {/* SMALL ACCENT BALL */}
          <span className="absolute bottom-6 left-8 w-14 h-14 rounded-full bg-slate-300 shadow-lg z-20" />
        </div>

        {/* RIGHT CONTENT */}
        <div>
          {/* SECTION BADGE */}
          <div className="flex items-center gap-3 mb-5">
            <span className="text-[#ff6b00] font-bold tracking-[0.2em] text-sm uppercase">
              About us
            </span>
          </div>

          {/* MAIN HEADING */}
          <h2
            className="text-[42px] md:text-[54px] lg:text-[58px] font-black text-[#06183f] leading-[1.05] mb-8"
            style={{
              fontFamily: "'Alegreya Sans', sans-serif",
            }}
          >
            We always provide the{" "}
            <span className="text-[#06183f]">
              best basic education
            </span>
            <br />
            to every Akwa Ibom child
          </h2>

          {/* PARAGRAPH + VIDEO */}
          <div className="grid grid-cols-[1fr_180px] gap-6 items-start">
            <p className="text-[#5b6478] leading-[1.95] text-[16px]">
              <span
                className="float-left text-[#06183f] font-black text-[78px] leading-none mr-3 mt-1"
                style={{
                  fontFamily: "'Alegreya Sans', sans-serif",
                }}
              >
                A
              </span>
              KSUBEB has been optimised to deliver inclusive,
              equitable and quality basic education — from policy
              and infrastructure to teacher development and
              learning materials — with a unique focus on every
              child of school age across the 31 LGAs of Akwa
              Ibom State.
            </p>

            {/* VIDEO CARD */}
            <button
              onClick={() => setVideoOpen(true)}
              className="group relative aspect-[3/4] overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-500"
              aria-label="Play AKSUBEB video"
            >
              {/* THUMB */}
              <img
                src={VIDEO_THUMB}
                alt="AKSUBEB at a glance"
                className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700 group-hover:scale-105"
              />

              {/* GREEN OVERLAY */}
              <span className="absolute inset-0 bg-[#1f9d4c]/45 group-hover:bg-[#ff6b00]/45 transition-all duration-500" />

              {/* PLAY BUTTON */}
              <span className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-16 h-16 rounded-full bg-[#1f9d4c] text-white flex items-center justify-center shadow-2xl group-hover:bg-[#ff6b00] group-hover:scale-110 transition-all duration-500">
                <Play
                  size={24}
                  fill="white"
                  className="ml-1"
                />
              </span>
            </button>
          </div>

          {/* BOTTOM STATS */}
          <div className="mt-10 grid grid-cols-2 border-t border-slate-200 pt-7">
            {/* FIRST */}
            <div className="group flex items-center gap-4 pr-4 border-r border-slate-200">
              <span className="w-14 h-14 flex items-center justify-center rounded-full bg-[#1f9d4c]/10 text-[#1f9d4c] group-hover:bg-[#ff6b00]/15 group-hover:text-[#ff6b00] transition-all duration-500">
                <GraduationCap
                  size={28}
                  strokeWidth={1.8}
                />
              </span>

              <span
                className="text-[#06183f] font-black text-[20px] leading-tight"
                style={{
                  fontFamily: "'Alegreya Sans', sans-serif",
                }}
              >
                Expert teaching <br />
                staff
              </span>
            </div>

            {/* SECOND */}
            <div className="group flex items-center gap-4 pl-6">
              <span className="w-14 h-14 flex items-center justify-center rounded-full bg-[#1f9d4c]/10 text-[#1f9d4c] group-hover:bg-[#ff6b00]/15 group-hover:text-[#ff6b00] transition-all duration-500">
                <Building2
                  size={28}
                  strokeWidth={1.8}
                />
              </span>

              <span
                className="text-[#0a2463] font-black text-[20px] leading-tight"
                style={{
                  fontFamily: "'Alegreya Sans', sans-serif",
                }}
              >
                Quality school <br />
                facilities
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* VIDEO MODAL */}
      {videoOpen && (
        <div
          onClick={() => setVideoOpen(false)}
          className="fixed inset-0 bg-black/85 z-[100] flex items-center justify-center p-4"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-5xl aspect-video bg-black shadow-2xl"
          >
            <button
              onClick={() => setVideoOpen(false)}
              className="absolute -top-12 right-0 text-white hover:text-[#ff6b00] transition-colors"
              aria-label="Close"
            >
              <X size={28} />
            </button>

            <iframe
              className="w-full h-full"
              src={VIDEO_URL}
              title="AKSUBEB"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        </div>
      )}
    </section>
  );
};

export default AboutSnippet;